import React, { useEffect, useState } from 'react';
import { bannerApi, uploadApi } from '../services/api';
import { Plus, Pencil, Trash2, Upload, Film, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_BANNER = {
  eyebrow: '', headline: '', subtitle: '', tagline: '',
  mediaType: 'image', imageUrl: '', videoUrl: '',
  primaryCtaLabel: '', primaryCtaTo: '', secondaryCtaLabel: '', secondaryCtaTo: '',
  order: 0, isActive: true,
};

export default function AdminBanners() {
  const [banners,  setBanners]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null); // null | { mode:'add'|'edit', data }
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await bannerApi.getAll();
    setBanners(res.data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadApi.upload(file, 'banners');
      setModal((m) => ({ ...m, data: { ...m.data, [field]: res.data.url } }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const { mode, data } = modal;
    try {
      if (mode === 'add') {
        await bannerApi.create(data);
        toast.success('Banner created');
      } else {
        await bannerApi.update(data.id, data);
        toast.success('Banner updated');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error saving banner');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    await bannerApi.delete(id);
    toast.success('Banner deleted');
    load();
  };

  const toggleActive = async (banner) => {
    await bannerApi.update(banner.id, { isActive: !banner.isActive });
    load();
  };

  const setField = (field, value) => setModal((m) => ({ ...m, data: { ...m.data, [field]: value } }));

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Homepage Banners</h1>
        <button className="btn btn-primary" onClick={() => setModal({ mode: 'add', data: { ...EMPTY_BANNER, order: banners.length } })}>
          <Plus size={18} /> Add Banner
        </button>
      </div>

      <p className="meta-text" style={{ marginBottom: 16 }}>
        Controls the hero carousel on the storefront homepage. Only active banners are shown, in order.
      </p>

      {loading ? <div className="spinner" /> : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order</th><th>Media</th><th>Headline</th><th>Type</th><th>Active</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {banners.map((b) => (
                <tr key={b.id}>
                  <td>{b.order}</td>
                  <td>
                    {b.imageUrl ? (
                      <img src={b.imageUrl} alt={b.headline} className="row-thumbnail" />
                    ) : (
                      <div className="row-thumbnail" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {b.mediaType === 'video' ? <Film size={20} /> : <ImageIcon size={20} />}
                      </div>
                    )}
                  </td>
                  <td>{b.headline}</td>
                  <td>{b.mediaType}</td>
                  <td>
                    <button className={`btn btn-sm ${b.isActive ? 'btn-primary' : 'btn-outline'}`} onClick={() => toggleActive(b)}>
                      {b.isActive ? 'Active' : 'Hidden'}
                    </button>
                  </td>
                  <td>
                    <button className="icon-btn" onClick={() => setModal({ mode: 'edit', data: { ...b } })}>
                      <Pencil size={16} />
                    </button>
                    <button className="icon-btn danger" onClick={() => remove(b.id)}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {banners.length === 0 && (
                <tr><td colSpan={6} className="empty-row">No banners yet — add one to populate the homepage carousel.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <h2>{modal.mode === 'add' ? 'Add Banner' : 'Edit Banner'}</h2>

            <div className="form-grid">
              <div className="form-field">
                <label>Eyebrow</label>
                <input value={modal.data.eyebrow} onChange={(e) => setField('eyebrow', e.target.value)} placeholder="e.g. Swift & Sure Exim" />
              </div>
              <div className="form-field">
                <label>Headline *</label>
                <input value={modal.data.headline} onChange={(e) => setField('headline', e.target.value)} placeholder="e.g. Golden Purity in Every Pinch" />
              </div>
              <div className="form-field">
                <label>Subtitle</label>
                <input value={modal.data.subtitle} onChange={(e) => setField('subtitle', e.target.value)} placeholder="e.g. Turmeric Powder" />
              </div>
              <div className="form-field">
                <label>Tagline</label>
                <input value={modal.data.tagline} onChange={(e) => setField('tagline', e.target.value)} placeholder="e.g. Pure • Hygienic • Export Quality" />
              </div>
              <div className="form-field">
                <label>Order</label>
                <input type="number" value={modal.data.order} onChange={(e) => setField('order', parseInt(e.target.value) || 0)} />
              </div>
              <div className="form-field">
                <label>Media Type</label>
                <select value={modal.data.mediaType} onChange={(e) => setField('mediaType', e.target.value)}>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label>{modal.data.mediaType === 'video' ? 'Poster Image (shown while video loads)' : 'Image *'}</label>
              <div className="image-upload-row">
                {modal.data.imageUrl && <img src={modal.data.imageUrl} alt="preview" className="upload-preview" />}
                <label className="upload-label">
                  <Upload size={18}/> {uploading ? 'Uploading…' : 'Upload Image'}
                  <input type="file" accept="image/*" hidden onChange={(e) => handleUpload(e, 'imageUrl')} />
                </label>
              </div>
            </div>

            {modal.data.mediaType === 'video' && (
              <div className="form-field">
                <label>Video *</label>
                <div className="image-upload-row">
                  {modal.data.videoUrl && <span className="meta-text">{modal.data.videoUrl}</span>}
                  <label className="upload-label">
                    <Upload size={18}/> {uploading ? 'Uploading…' : 'Upload Video (mp4)'}
                    <input type="file" accept="video/mp4" hidden onChange={(e) => handleUpload(e, 'videoUrl')} />
                  </label>
                </div>
              </div>
            )}

            <div className="form-grid">
              <div className="form-field">
                <label>Primary Button Label</label>
                <input value={modal.data.primaryCtaLabel} onChange={(e) => setField('primaryCtaLabel', e.target.value)} placeholder="e.g. Shop Products" />
              </div>
              <div className="form-field">
                <label>Primary Button Link</label>
                <input value={modal.data.primaryCtaTo} onChange={(e) => setField('primaryCtaTo', e.target.value)} placeholder="e.g. /products" />
              </div>
              <div className="form-field">
                <label>Secondary Button Label</label>
                <input value={modal.data.secondaryCtaLabel} onChange={(e) => setField('secondaryCtaLabel', e.target.value)} placeholder="e.g. Explore More" />
              </div>
              <div className="form-field">
                <label>Secondary Button Link</label>
                <input value={modal.data.secondaryCtaTo} onChange={(e) => setField('secondaryCtaTo', e.target.value)} placeholder="e.g. /about" />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={uploading}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
